// src/pages/SystemResources/index.js — "Servidor" (super): CPU, memória, discos, processo e banco
import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import LinearProgress from "@material-ui/core/LinearProgress";
import Switch from "@material-ui/core/Switch";
import Skeleton from "@material-ui/lab/Skeleton";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import RefreshOutlinedIcon from "@material-ui/icons/RefreshOutlined";
import SpeedOutlinedIcon from "@material-ui/icons/SpeedOutlined";
import StorageIcon from "@material-ui/icons/Storage";
import DnsOutlinedIcon from "@material-ui/icons/DnsOutlined";

import MainContainer from "../../components/MainContainer";
import { EmptyState, PageHeader, Section, StatCard, StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import useStyles from "./styles";

const REFRESH_MS = 15000;

export const formatBytes = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: i >= 3 ? 1 : 0 })} ${units[i]}`;
};

export const formatUptime = (sec) => {
  if (!Number.isFinite(Number(sec))) return "-";
  const s = Math.max(0, Math.floor(Number(sec)));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

const pctText = (v) => (v === null || v === undefined ? "-" : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`);

/** verde < 70 %, amarelo < 90 %, vermelho acima */
export const toneFor = (pct) => {
  if (pct === null || pct === undefined) return "neutral";
  if (pct >= 90) return "error";
  if (pct >= 70) return "warning";
  return "success";
};

const UsageBar = ({ label, ariaLabel, pct, hint, classes }) => {
  const tone = toneFor(pct);
  const cls =
    tone === "error"
      ? classes.barCrit
      : tone === "warning"
      ? classes.barWarn
      : tone === "neutral"
      ? classes.barNeutral
      : classes.barOk;
  const pctCls = tone === "error" ? classes.pctCrit : tone === "warning" ? classes.pctWarn : null;
  return (
    <div className={classes.barBlock}>
      <div className={classes.barHead}>
        <span className={classes.barLabel}>{label}</span>
        <span className={clsx(classes.barPct, pctCls)}>{pctText(pct)}</span>
      </div>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, Number(pct) || 0))}
        className={clsx(classes.bar, cls)}
        aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
      />
      {hint && <span className={classes.barHint}>{hint}</span>}
    </div>
  );
};

const Row = ({ label, value, classes }) => (
  <div className={classes.row}>
    <dt className={classes.rowLabel}>{label}</dt>
    <dd className={classes.rowValue} title={typeof value === "string" ? value : undefined}>
      {value}
    </dd>
  </div>
);

const SystemResources = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auto, setAuto] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: res } = await api.get("/system/resources");
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "error");
      if (!silent) toastError(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (auto) timer.current = setInterval(() => load(true), REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [auto, load]);

  const t = (k, opts) => i18n.t(`systemResources.${k}`, opts);
  const mainDisk = data?.disks?.find((d) => d.mount === "/") || data?.disks?.[0] || null;
  const collectedAt = data?.collectedAt ? new Date(data.collectedAt).toLocaleTimeString("pt-BR") : null;

  return (
    <MainContainer maxWidth>
      <PageHeader
        title={t("title")}
        subtitle={data ? `${data.hostname} · ${data.platform}` : t("subtitle")}
        actions={
          <div className={classes.actions}>
            {collectedAt && <span className={classes.meta}>{t("collectedAt", { time: collectedAt })}</span>}
            <FormControlLabel
              className={classes.autoLabel}
              control={<Switch size="small" color="primary" checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
              label={t("autoRefresh")}
            />
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}
              disabled={loading}
              onClick={() => load(false)}
            >
              {t("refresh")}
            </Button>
          </div>
        }
      />
      <div className={classes.scroll}>
        {error && !data && (
          <Section>
            <EmptyState
              icon={<ErrorOutlineIcon />}
              title={t("error")}
              action={
                <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={() => load(false)}>
                  {t("refresh")}
                </Button>
              }
            />
          </Section>
        )}
        {!data && !error && (
          <div className={classes.cards} aria-busy="true" aria-label={t("loading")}>
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} variant="rect" height={104} className={classes.skeletonCard} />
            ))}
          </div>
        )}
        {data && (
          <>
            <div className={classes.cards}>
              <StatCard
                label={t("cpu")}
                value={pctText(data.cpu.usagePct)}
                icon={<SpeedOutlinedIcon />}
                tone={toneFor(data.cpu.usagePct)}
                hint={t("cpuHint", { cores: data.cpu.cores, load: data.cpu.loadAvg[0] })}
              />
              <StatCard
                label={t("memory")}
                value={pctText(data.memory.usedPct)}
                icon={<MemoryOutlinedIcon />}
                tone={toneFor(data.memory.usedPct)}
                hint={`${formatBytes(data.memory.usedBytes)} / ${formatBytes(data.memory.totalBytes)}`}
              />
              <StatCard
                label={t("disk")}
                value={mainDisk ? pctText(mainDisk.usedPct) : "-"}
                icon={<StorageIcon />}
                tone={toneFor(mainDisk?.usedPct)}
                hint={mainDisk ? `${formatBytes(mainDisk.usedBytes)} / ${formatBytes(mainDisk.sizeBytes)} (${mainDisk.mount})` : t("noDisk")}
              />
              <StatCard
                label={t("uptime")}
                value={formatUptime(data.uptimeSec)}
                icon={<DnsOutlinedIcon />}
                tone="brand"
                hint={t("processUptime", { time: formatUptime(data.process.uptimeSec) })}
              />
            </div>

            <div className={classes.grid}>
              <Section
                title={t("cpuSection")}
                actions={auto && <StatusBadge color="success" label={t("live")} />}
              >
                <div className={classes.stack}>
                  <UsageBar label={t("cpuUsage")} pct={data.cpu.usagePct} classes={classes} hint={t("cpuSample")} />
                  <dl className={classes.rows}>
                    <Row label={t("cpuModel")} value={data.cpu.model} classes={classes} />
                    <Row label={t("cores")} value={data.cpu.cores} classes={classes} />
                    <Row label={t("loadAvg")} value={data.cpu.loadAvg.join(" · ")} classes={classes} />
                  </dl>
                </div>
              </Section>

              <Section title={t("memorySection")}>
                <div className={classes.stack}>
                  <UsageBar
                    label={t("ram")}
                    pct={data.memory.usedPct}
                    classes={classes}
                    hint={t("memoryHint", { available: formatBytes(data.memory.availableBytes) })}
                  />
                  {data.memory.swapTotalBytes > 0 && (
                    <UsageBar
                      label={t("swap")}
                      pct={(data.memory.swapUsedBytes / data.memory.swapTotalBytes) * 100}
                      classes={classes}
                      hint={`${formatBytes(data.memory.swapUsedBytes)} / ${formatBytes(data.memory.swapTotalBytes)}`}
                    />
                  )}
                  <dl className={classes.rows}>
                    <Row label={t("total")} value={formatBytes(data.memory.totalBytes)} classes={classes} />
                    <Row label={t("used")} value={formatBytes(data.memory.usedBytes)} classes={classes} />
                    <Row label={t("available")} value={formatBytes(data.memory.availableBytes)} classes={classes} />
                  </dl>
                </div>
              </Section>

              <Section title={t("disksSection")}>
                {data.disks.length === 0 ? (
                  <div className={classes.empty}>{t("noDisk")}</div>
                ) : (
                  <div className={classes.stack}>
                    {data.disks.map((d) => (
                      <UsageBar
                        key={`${d.filesystem}-${d.mount}`}
                        ariaLabel={`${d.mount} (${d.filesystem})`}
                        label={
                          <span>
                            <span className={classes.diskMount}>{d.mount}</span>
                            <span className={classes.diskFs}>{d.filesystem}</span>
                          </span>
                        }
                        pct={d.usedPct}
                        classes={classes}
                        hint={t("diskHint", { used: formatBytes(d.usedBytes), size: formatBytes(d.sizeBytes), free: formatBytes(d.availBytes) })}
                      />
                    ))}
                  </div>
                )}
              </Section>

              <Section title={t("processSection")}>
                <dl className={classes.rows}>
                  <Row label={t("node")} value={data.process.nodeVersion} classes={classes} />
                  <Row label="PID" value={data.process.pid} classes={classes} />
                  <Row label={t("processUptimeLabel")} value={formatUptime(data.process.uptimeSec)} classes={classes} />
                  <Row label={t("rss")} value={formatBytes(data.process.rssBytes)} classes={classes} />
                  <Row label={t("heap")} value={`${formatBytes(data.process.heapUsedBytes)} / ${formatBytes(data.process.heapTotalBytes)}`} classes={classes} />
                  <Row label={t("database")} value={data.database.name || "-"} classes={classes} />
                  <Row label={t("databaseSize")} value={formatBytes(data.database.sizeBytes)} classes={classes} />
                </dl>
              </Section>
            </div>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default SystemResources;
